import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { API_BASE } from "@/lib/api";
import { openFile } from "@/lib/fileViewer";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import EventFormDialog from "@/components/EventFormDialog";
import { Plus, ChevronLeft, ChevronRight, Calendar as CalIcon, MapPin, Users, Trash2, Repeat, Filter, FileDown, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth, isStudent, isAdminOrSegreteria } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { WEEKDAYS_IT } from "@/components/school/CalendarRevealCard";

const KIND_LABEL = {
  lezione: "Lezione",
  riposo: "Riposo",
  uscita_missionaria: "Uscita",
  viaggio_missionario: "Viaggio",
  esame: "Esame",
  pranzo: "Pranzo",
  preghiera: "Preghiera",
  altro: "Evento",
};
const DAYS_IT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const DEFAULT_ACADEMIC_YEAR = "2026/2027";

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function iso(d) {
  // Usa la data LOCALE (non UTC) per evitare l'offset di fuso che fa slittare il giorno
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fmtDayIt(d) { return d.toLocaleDateString("it-IT", { day: "numeric", month: "short" }); }

export default function Piano() {
  const { user } = useAuth();
  const student = isStudent(user);
  const canManage = isAdminOrSegreteria(user);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(new Date());
  const [view, setViewRaw] = useState(() => (!student && window.innerWidth < 768 ? "day" : "week"));
  const setView = (v) => setViewRaw(student ? "week" : v);
  const { data: reveal } = useQuery({
    queryKey: ["calendar-reveal"],
    queryFn: async () => (await api.get("/school-config/calendar-reveal")).data,
    enabled: student,
  });
  const studentLimit = useMemo(() => {
    if (reveal?.visible_until) return reveal.visible_until;
    return iso(addDays(startOfWeek(new Date()), 6));
  }, [reveal]);
  const revealDay = WEEKDAYS_IT[reveal?.weekday ?? 4];
  const [createOpen, setCreateOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [delEvent, setDelEvent] = useState(null);
  const [defaultDate, setDefaultDate] = useState("");
  const [scopePrompt, setScopePrompt] = useState(null); // { mode:"delete"|"edit", event, payload? }

  // Filtri staff
  const [fAcademicYear, setFAcademicYear] = useState(DEFAULT_ACADEMIC_YEAR);
  const [fTrack, setFTrack] = useState("all");
  const [fYear, setFYear] = useState("all");
  const [newAyOpen, setNewAyOpen] = useState(false);
  const [newAy, setNewAy] = useState("");

  const { data: academicYears = [DEFAULT_ACADEMIC_YEAR] } = useQuery({
    queryKey: ["academic-years"],
    queryFn: async () => {
      const r = (await api.get("/schedule/academic-years")).data;
      return r?.length ? r : [DEFAULT_ACADEMIC_YEAR];
    },
    enabled: !student,
  });
  // include also current selection if not in list yet
  const ayOptions = useMemo(() => {
    const s = new Set(academicYears);
    if (fAcademicYear) s.add(fAcademicYear);
    return Array.from(s).sort((a, b) => b.localeCompare(a));
  }, [academicYears, fAcademicYear]);

  const range = useMemo(() => {
    if (view === "day") return { from: iso(anchor), to: iso(anchor), days: [new Date(anchor)] };
    if (view === "week") {
      const s = startOfWeek(anchor);
      return { from: iso(s), to: iso(addDays(s, 6)), days: Array.from({ length: 7 }, (_, i) => addDays(s, i)) };
    }
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    const gridStart = startOfWeek(first);
    const gridEndDate = new Date(last);
    const padDays = 6 - ((gridEndDate.getDay() + 6) % 7);
    const gridEnd = addDays(gridEndDate, padDays);
    const days = [];
    for (let d = new Date(gridStart); d <= gridEnd; d = addDays(d, 1)) days.push(new Date(d));
    return { from: iso(gridStart), to: iso(gridEnd), days };
  }, [anchor, view]);

  const params = useMemo(() => {
    const p = { date_from: range.from, date_to: range.to };
    if (!student) {
      if (fAcademicYear) p.academic_year = fAcademicYear;
      if (fTrack !== "all") p.track_type = fTrack;
      if (fYear !== "all") p.year_level = fYear;
    }
    return p;
  }, [range.from, range.to, fAcademicYear, fTrack, fYear, student]);

  const { data: events = [], isLoading } = useQuery({
    queryKey: ["schedule", params, student],
    queryFn: async () => (await api.get("/schedule/events", { params })).data,
  });

  const createMut = useMutation({
    mutationFn: async (p) => (await api.post("/schedule/events", p)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["schedule"] });
      toast.success(d.created > 1 ? `Serie creata: ${d.created} occorrenze` : "Evento creato");
      // Auto-navigate al primo evento creato per farlo comparire in vista corrente
      const targetDate = d.first_date || d.event?.date;
      if (targetDate) setAnchor(new Date(targetDate));
      setCreateOpen(false);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const editMut = useMutation({
    mutationFn: async ({ id, p, scope }) => (await api.patch(`/schedule/events/${id}`, p, { params: { scope } })).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["schedule"] });
      toast.success(d.updated > 1 ? `${d.updated} occorrenze aggiornate` : "Evento aggiornato");
      setEditEvent(null); setScopePrompt(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const delMut = useMutation({
    mutationFn: async ({ id, scope }) => (await api.delete(`/schedule/events/${id}`, { params: { scope } })).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["schedule"] });
      toast.success(d.deleted > 1 ? `${d.deleted} occorrenze eliminate` : "Evento eliminato");
      setDelEvent(null); setScopePrompt(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const attendanceMut = useMutation({
    mutationFn: async (eventId) => (await api.post(`/schedule/events/${eventId}/attendance-session`)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["schedule"] });
      toast.success(d.created ? "Sessione presenze creata" : "Sessione presenze aperta");
      navigate(`/scuola/presenze/${d.id}/qr`);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const e of events) (map[e.date] ||= []).push(e);
    return map;
  }, [events]);

  const nextLocked = student && iso(addDays(startOfWeek(anchor), 7)) > studentLimit;
  const nav = (delta) => {
    if (delta > 0 && nextLocked) return;
    const n = new Date(anchor);
    if (view === "day") n.setDate(n.getDate() + delta);
    else if (view === "week") n.setDate(n.getDate() + delta * 7);
    else n.setMonth(n.getMonth() + delta);
    setAnchor(n);
  };
  const goToday = () => setAnchor(new Date());

  const headerTitle = view === "day"
    ? anchor.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : view === "week"
    ? `Settimana ${fmtDayIt(range.days[0])} — ${fmtDayIt(range.days[6])}`
    : anchor.toLocaleDateString("it-IT", { month: "long", year: "numeric" });

  // Export PDF: apre nuova tab con URL autenticato via ?auth= (studente usa la propria autenticazione)
  function exportPdf(scope) {
    const token = localStorage.getItem("token");
    const params = new URLSearchParams();
    params.set("scope", scope);
    if (scope === "day") {
      // usa la data corrente selezionata (anchor) o oggi
      params.set("date_from", iso(anchor));
    } else {
      params.set("date_from", range.from);
      params.set("date_to", range.to);
    }
    if (!student) {
      if (fAcademicYear) params.set("academic_year", fAcademicYear);
      if (fTrack !== "all") params.set("track_type", fTrack);
      if (fYear !== "all") params.set("year_level", fYear);
    }
    params.set("auth", token || "");
    const url = `${API_BASE}/schedule/events/export.pdf?${params.toString()}`;
    openFile(url, "Calendario.pdf");
  }

  // Gestione modifica/eliminazione con serie ricorrente
  function handleEditSubmit(payload) {
    if (editEvent?.series_id) {
      setScopePrompt({ mode: "edit", event: editEvent, payload });
    } else {
      editMut.mutate({ id: editEvent.id, p: payload, scope: "single" });
    }
  }
  function handleDeleteConfirm() {
    if (delEvent?.series_id) {
      setScopePrompt({ mode: "delete", event: delEvent });
    } else {
      delMut.mutate({ id: delEvent.id, scope: "single" });
    }
  }
  function applyScope(scope) {
    if (!scopePrompt) return;
    if (scopePrompt.mode === "edit") {
      editMut.mutate({ id: scopePrompt.event.id, p: scopePrompt.payload, scope });
    } else {
      delMut.mutate({ id: scopePrompt.event.id, scope });
    }
  }

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1500px] mx-auto">
      <PageHeader
        title="Calendario didattico"
        subtitle={student
          ? "Il tuo calendario personale: lezioni, esami, uscite, viaggi e attività ricorrenti."
          : "Costruisci il calendario didattico: lezioni, riposo, uscite e viaggi missionari, con supporto per attività ricorrenti."}
        actions={canManage && (
          <Button
            data-testid="event-add-btn"
            onClick={() => { setDefaultDate(iso(new Date())); setCreateOpen(true); }}
            className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]"
          >
            <Plus size={16} className="mr-1.5"/> Aggiungi evento
          </Button>
        )}
      />

      {!student && (
        <Card className="p-3 mb-4 border border-stone-200 rounded-xl shadow-none bg-white">
          <div className="flex flex-col md:flex-row md:items-end gap-3">
            <div className="flex items-center gap-2 text-xs font-medium text-stone-500 uppercase tracking-widest">
              <Filter size={12}/> Filtri
            </div>
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-stone-500">Anno accademico</Label>
                <Select
                  value={fAcademicYear}
                  onValueChange={(v) => {
                    if (v === "__new") { setNewAyOpen(true); return; }
                    setFAcademicYear(v);
                  }}
                >
                  <SelectTrigger data-testid="filter-academic-year" className="mt-1"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {ayOptions.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                    <SelectItem value="__new">+ Nuovo anno accademico…</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-stone-500">Percorso</Label>
                <Select value={fTrack} onValueChange={setFTrack}>
                  <SelectTrigger data-testid="filter-track" className="mt-1"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="residenziale">Residenziale</SelectItem>
                    <SelectItem value="modulare">Modulare</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-stone-500">Anno di corso</Label>
                <Select value={fYear} onValueChange={setFYear}>
                  <SelectTrigger data-testid="filter-year" className="mt-1"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    <SelectItem value="1">I anno</SelectItem>
                    <SelectItem value="2">II anno</SelectItem>
                    <SelectItem value="3">III anno</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => nav(-1)} data-testid="cal-prev" className="rounded-full h-9 w-9 p-0"><ChevronLeft size={15}/></Button>
          <Button size="sm" variant="outline" onClick={goToday} data-testid="cal-today" className="rounded-full">Oggi</Button>
          <Button size="sm" variant="outline" onClick={() => nav(1)} disabled={nextLocked} title={nextLocked ? `La settimana successiva sarà visibile da ${revealDay}` : undefined} data-testid="cal-next" className="rounded-full h-9 w-9 p-0"><ChevronRight size={15}/></Button>
          <div className="ml-3 text-sm font-medium text-[#1C352D] capitalize flex items-center gap-1.5">
            <CalIcon size={14}/> {headerTitle}
          </div>
        </div>
        {!student && (
        <Select value={view} onValueChange={setView}>
          <SelectTrigger className="w-40" data-testid="cal-view"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="day" data-testid="cal-view-day">Giorno</SelectItem>
            <SelectItem value="week">Settimana</SelectItem>
            <SelectItem value="month">Mese</SelectItem>
          </SelectContent>
        </Select>
        )}
        <div className="flex items-center gap-2">
          {!student && (
          <Button
            variant="outline"
            size="sm"
            data-testid="export-pdf-day"
            onClick={() => exportPdf("day")}
            className="rounded-full"
          >
            <FileDown size={13} className="mr-1.5"/> PDF giornaliero
          </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            data-testid="export-pdf-week"
            onClick={() => exportPdf("week")}
            className="rounded-full"
          >
            <FileDown size={13} className="mr-1.5"/> PDF settimanale
          </Button>
        </div>
      </div>

      {nextLocked && (
        <div className="mb-3 text-xs text-stone-500" data-testid="cal-next-locked-note">La settimana successiva sarà visibile da {revealDay}.</div>
      )}
      {view === "day" ? (
        <DayView
          day={anchor}
          events={eventsByDay[iso(anchor)] || []}
          student={student}
          onPrev={() => nav(-1)}
          onNext={() => nav(1)}
          onAdd={() => { setDefaultDate(iso(anchor)); setCreateOpen(true); }}
          onEdit={setEditEvent}
          onDelete={setDelEvent}
          onAttendance={(id) => attendanceMut.mutate(id)}
        />
      ) : view === "week" ? (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-2 md:gap-3" data-testid="week-grid">
          {range.days.map((d, i) => {
            const key = iso(d);
            const dayEvents = eventsByDay[key] || [];
            const isToday = key === iso(new Date());
            return (
              <Card
                key={key}
                data-testid={`day-col-${key}`}
                className={`md:min-h-[280px] p-3 border rounded-xl shadow-none ${isToday ? "border-[#1C352D] bg-[#FDFBF7]" : "border-stone-200 bg-white"}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div
                    className="flex items-baseline gap-2 md:block cursor-pointer"
                    onClick={() => { if (!student) { setAnchor(d); setView("day"); } }}
                    data-testid={`day-open-${key}`}
                  >
                    <div className="text-[10px] uppercase tracking-widest text-stone-500">{DAYS_IT[i]}</div>
                    <div className={`text-lg font-semibold ${isToday ? "text-[#E2725B]" : "text-[#1C352D]"}`}>{d.getDate()}</div>
                  </div>
                  {!student && (
                    <button
                      onClick={() => { setDefaultDate(key); setCreateOpen(true); }}
                      data-testid={`day-add-${key}`}
                      className="w-6 h-6 rounded-full bg-stone-100 hover:bg-[#1C352D] hover:text-white text-stone-500 grid place-items-center text-lg"
                    >+</button>
                  )}
                </div>
                <div className="space-y-1.5">
                  {dayEvents.length === 0 ? (
                    <div className="text-[11px] text-stone-400 py-1 md:py-4 text-left md:text-center">—</div>
                  ) : dayEvents.map((e) => (
                    <EventCard
                      key={e.id}
                      evt={e}
                      onClick={() => !student && setEditEvent(e)}
                      onDelete={() => !student && setDelEvent(e)}
                      onAttendance={() => !student && attendanceMut.mutate(e.id)}
                      student={student}
                    />
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-2">
          {DAYS_IT.map((d) => <div key={d} className="text-[10px] uppercase tracking-widest text-stone-500 text-center py-2">{d}</div>)}
          {range.days.map((d) => {
            const key = iso(d);
            const dayEvents = eventsByDay[key] || [];
            const isCurrent = d.getMonth() === anchor.getMonth();
            const isToday = key === iso(new Date());
            return (
              <Card
                key={key}
                data-testid={`month-cell-${key}`}
                onClick={() => { setAnchor(d); setView("day"); }}
                className={`min-h-[110px] p-2 border rounded-md shadow-none cursor-pointer hover:border-[#1C352D] transition-colors ${
                  isCurrent ? "bg-white" : "bg-stone-50/50 opacity-60"
                } ${isToday ? "border-[#E2725B] border-2" : "border-stone-200"}`}
              >
                <div className={`text-xs font-medium ${isToday ? "text-[#E2725B]" : "text-stone-600"}`}>{d.getDate()}</div>
                <div className="mt-1 space-y-0.5">
                  {dayEvents.slice(0, 3).map((e) => (
                    <div
                      key={e.id}
                      onClick={(ev) => { if (!student && window.innerWidth >= 768) { ev.stopPropagation(); setEditEvent(e); } }}
                      data-testid={`month-event-${e.id}`}
                      className="text-[10px] px-1.5 py-0.5 rounded truncate text-white flex items-center gap-1 cursor-pointer"
                      style={{ background: e.color }}
                    >
                      {e.series_id && <Repeat size={8}/>}
                      {e.start_time && !e.all_day ? `${e.start_time.slice(0, 5)} ` : ""}{e.title}
                    </div>
                  ))}
                  {dayEvents.length > 3 && <div className="text-[10px] text-stone-500">+{dayEvents.length - 3}</div>}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {isLoading && <div className="text-center text-stone-400 text-sm py-4">Caricamento…</div>}

      <EventFormDialog open={createOpen} onOpenChange={setCreateOpen} mode="create" defaultDate={defaultDate}
        submitting={createMut.isPending} onSubmit={(p) => createMut.mutate(p)}/>
      <EventFormDialog open={!!editEvent} onOpenChange={(v) => !v && setEditEvent(null)} mode="edit" initial={editEvent}
        submitting={editMut.isPending} onSubmit={handleEditSubmit}/>

      <AlertDialog open={!!delEvent} onOpenChange={(v) => !v && setDelEvent(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l&apos;evento &laquo;{delEvent?.title}&raquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              {delEvent?.series_id
                ? "Questo evento fa parte di una serie ricorrente. Al passo successivo potrai scegliere se eliminare solo questa occorrenza, tutte le future o l'intera serie."
                : "L'operazione non è reversibile."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="event-del-confirm" className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDeleteConfirm}>
              {delEvent?.series_id ? "Continua" : "Elimina"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!scopePrompt} onOpenChange={(v) => !v && setScopePrompt(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <span className="flex items-center gap-2"><Repeat size={16}/> Evento ricorrente</span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              Vuoi {scopePrompt?.mode === "delete" ? "eliminare" : "aggiornare"} solo questa occorrenza,
              tutte le occorrenze future oppure l&apos;intera serie?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-1 gap-2 mt-2">
            <Button data-testid="scope-single" variant="outline" onClick={() => applyScope("single")}>Solo questa occorrenza</Button>
            <Button data-testid="scope-future" variant="outline" onClick={() => applyScope("future")}>Questa e tutte quelle future</Button>
            <Button data-testid="scope-all" className="bg-[#1C352D] hover:bg-[#264A3E]" onClick={() => applyScope("all")}>Tutta la serie</Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={newAyOpen} onOpenChange={setNewAyOpen}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle>Nuovo anno accademico</DialogTitle>
          </DialogHeader>
          <Label className="text-xs">Formato: AAAA/AAAA</Label>
          <Input
            data-testid="new-ay-input"
            value={newAy}
            onChange={(e) => setNewAy(e.target.value)}
            placeholder="Es. 2027/2028"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewAyOpen(false)}>Annulla</Button>
            <Button
              data-testid="new-ay-submit"
              className="bg-[#1C352D] hover:bg-[#264A3E]"
              onClick={() => {
                if (!/^\d{4}\/\d{4}$/.test(newAy)) {
                  toast.error("Formato non valido, usa AAAA/AAAA");
                  return;
                }
                setFAcademicYear(newAy);
                setNewAy("");
                setNewAyOpen(false);
              }}
            >
              Usa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DayView({ day, events, student, onPrev, onNext, onAdd, onEdit, onDelete, onAttendance }) {
  const touch = React.useRef(null);
  const sorted = [...events].sort((a, b) => (a.all_day ? -1 : 0) - (b.all_day ? -1 : 0) || (a.start_time || "").localeCompare(b.start_time || ""));
  const onTouchStart = (e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchEnd = (e) => {
    if (!touch.current) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    touch.current = null;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) (dx < 0 ? onNext : onPrev)();
  };
  const isToday = iso(day) === iso(new Date());
  return (
    <Card
      data-testid="day-view"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className={`p-4 md:p-6 border rounded-xl shadow-none min-h-[360px] ${isToday ? "border-[#1C352D] bg-[#FDFBF7]" : "border-stone-200 bg-white"}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-stone-500">{day.toLocaleDateString("it-IT", { weekday: "long" })}</div>
          <div className={`text-3xl font-semibold ${isToday ? "text-[#E2725B]" : "text-[#1C352D]"}`}>{day.getDate()}</div>
        </div>
        {!student && (
          <Button size="sm" onClick={onAdd} data-testid="day-view-add" className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]">
            <Plus size={14} className="mr-1"/> Evento
          </Button>
        )}
      </div>
      {sorted.length === 0 ? (
        <div className="text-sm text-stone-400 py-12 text-center" data-testid="day-view-empty">Nessun evento in questo giorno</div>
      ) : (
        <div className="space-y-2.5">
          {sorted.map((e) => (
            <EventCard key={e.id} evt={e} large student={student}
              onClick={() => !student && onEdit(e)} onDelete={() => onDelete(e)} onAttendance={() => onAttendance(e.id)} />
          ))}
        </div>
      )}
      <div className="md:hidden text-[11px] text-stone-400 text-center mt-6">Scorri a destra o sinistra per cambiare giorno</div>
    </Card>
  );
}

function EventCard({ evt, onClick, onDelete, onAttendance, student, large }) {
  const isLezione = evt.kind === "lezione";
  return (
    <div
      data-testid={`event-${evt.id}`}
      onClick={onClick}
      className={`group relative rounded-md text-white leading-tight ${large ? "p-3.5 pr-16 text-sm" : "p-2 text-[11px]"} ${!student ? "cursor-pointer" : ""}`}
      style={{ background: evt.color }}
    >
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest opacity-80 mb-0.5">
        {evt.series_id && <Repeat size={9}/>}
        <span>{KIND_LABEL[evt.kind] || evt.kind}</span>
        {evt.start_time && !evt.all_day && <span>· {evt.start_time.slice(0, 5)}{evt.end_time ? `–${evt.end_time.slice(0, 5)}` : ""}</span>}
      </div>
      <div className="font-medium">{evt.title}</div>
      {evt.location && <div className="opacity-80 mt-0.5 flex items-center gap-1"><MapPin size={9}/> {evt.location}</div>}
      {evt.teachers?.length > 0 && <div className="opacity-80 mt-0.5 truncate flex items-center gap-1"><Users size={9}/> {evt.teachers.join(", ")}</div>}
      {evt.attendance_session_id && (
        <div className="opacity-80 mt-0.5 text-[9px] flex items-center gap-1"><ClipboardCheck size={9}/> Presenze collegate</div>
      )}
      {!student && (
        <div className={`absolute top-1 right-1 flex gap-0.5 ${large ? "opacity-100 gap-1 top-2 right-2" : "opacity-100 md:opacity-0 md:group-hover:opacity-100"}`}>
          {isLezione && (
            <button
              onClick={(e) => { e.stopPropagation(); onAttendance(); }}
              data-testid={`event-attendance-${evt.id}`}
              title={evt.attendance_session_id ? "Apri sessione presenze" : "Crea sessione presenze"}
              className="bg-black/30 hover:bg-black/50 rounded p-0.5"
            ><ClipboardCheck size={10}/></button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            title="Elimina"
            className="bg-black/30 hover:bg-black/50 rounded p-0.5"
          ><Trash2 size={10}/></button>
        </div>
      )}
    </div>
  );
}
